import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DataTable from '../components/common/DataTable';
import { NumberTextField } from '../components/common';
import { insuranceCompanyService } from '../services/insuranceCompanyService';
import { servicePackageService } from '../services/servicePackageService';
import { useTranslation } from 'react-i18next';

const InsuranceCompanies: React.FC = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<any>(null);
  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [companyRates, setCompanyRates] = useState<{ [packageId: string]: { [serviceCode: string]: string } }>({});

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    excelStructure: 'standard',
    contractStartDate: '',
    contractEndDate: '',
    notes: '',
    isActive: true
  });

  useEffect(() => {
    fetchCompanies();
    // Only fetch service packages when needed (when editing)
  }, []);

  const fetchServicePackages = async () => {
    // Only fetch if we don't have them yet
    if (servicePackages.length > 0) return;
    
    try {
      const response = await servicePackageService.getAll();
      const packagesData = response?.data || response || [];
      setServicePackages(Array.isArray(packagesData) ? packagesData : []);
    } catch (error) {
      console.error('Error fetching service packages:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await insuranceCompanyService.getAll();
      // Handle Strapi v4 response format: { data: [...], meta: {...} }
      const companiesData = response?.data || response || [];
      const companiesArray = Array.isArray(companiesData) ? companiesData : [];
      // Normalize companies to handle both formats (with and without attributes)
      const normalizedCompanies = companiesArray.map((company: any) => {
        // If company has attributes, keep it as is (DataTable handles it)
        // Otherwise, return as is
        return company;
      });
      setCompanies(normalizedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (company: any = null) => {
    if (company) {
      // Get company ID first
      const companyId = company.id || company.attributes?.id;
      
      // Fetch service packages and company details in parallel (only if needed)
      const [fullCompanyResult, packagesResult] = await Promise.all([
        // Try to fetch full company details if we don't have complete data
        (async () => {
          let fullCompany = company;
          if (companyId && (!company.attributes || !company.attributes.name)) {
            try {
              const fetchedCompany = await insuranceCompanyService.getOne(companyId);
              fullCompany = fetchedCompany;
            } catch (error) {
              console.error('Error fetching company details:', error);
              // Use the company we have
            }
          }
          return fullCompany;
        })(),
        // Fetch service packages if we don't have them yet
        (async () => {
          if (servicePackages.length === 0) {
            try {
              const response = await servicePackageService.getAll();
              const packagesData = response?.data || response || [];
              const packagesArray = Array.isArray(packagesData) ? packagesData : [];
              setServicePackages(packagesArray);
              return packagesArray;
            } catch (error) {
              console.error('Error fetching service packages:', error);
              return [];
            }
          }
          return servicePackages;
        })()
      ]);
      
      const fullCompany = fullCompanyResult;
      const loadedPackages = packagesResult;
      setCurrentCompany(fullCompany);
      // Handle Strapi v4 data structure (attributes)
      const companyData = fullCompany.attributes || fullCompany;
      
      setFormData({
        name: companyData.name || '',
        code: companyData.code || '',
        contactPerson: companyData.contactPerson || '',
        email: companyData.email || '',
        phone: companyData.phone || '',
        address: companyData.address || '',
        excelStructure: companyData.excelStructure || 'standard',
        contractStartDate: companyData.contractStartDate || '',
        contractEndDate: companyData.contractEndDate || '',
        notes: companyData.notes || '',
        isActive: companyData.isActive ?? true
      });
      
      // Load company-specific rates from all service packages
      if (companyId) {
        const rates: { [packageId: string]: { [serviceCode: string]: string } } = {};
        for (const pkg of loadedPackages) {
          const packageId = pkg.id || pkg.attributes?.id;
          const packageData = pkg.attributes || pkg;
          if (packageData.companyRates) {
            const companyRatesData = typeof packageData.companyRates === 'string' 
              ? JSON.parse(packageData.companyRates) 
              : packageData.companyRates;
            if (companyRatesData[companyId]) {
              rates[packageId] = { ...companyRatesData[companyId] };
              // Ensure extra_km exists for this package
              if (!rates[packageId]['extra_km']) {
                rates[packageId]['extra_km'] = '';
              }
            } else {
              // Initialize with extra_km if no rates exist for this company
              rates[packageId] = { extra_km: '' };
            }
          } else {
            // Initialize with extra_km if package has no companyRates
            rates[packageId] = { extra_km: '' };
          }
        }
        setCompanyRates(rates);
      } else {
        setCompanyRates({});
      }
    } else {
      setCurrentCompany(null);
      setFormData({
        name: '',
        code: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        excelStructure: 'standard',
        contractStartDate: '',
        contractEndDate: '',
        notes: '',
        isActive: true
      });
      // Initialize with extra_km for all packages when creating new company
      const initialRates: { [packageId: string]: { [serviceCode: string]: string } } = {};
      // Fetch packages if not already loaded
      if (servicePackages.length === 0) {
        try {
          const response = await servicePackageService.getAll();
          const packagesData = response?.data || response || [];
          const packagesArray = Array.isArray(packagesData) ? packagesData : [];
          setServicePackages(packagesArray);
          packagesArray.forEach((pkg: any) => {
            const packageId = pkg.id || pkg.attributes?.id;
            if (packageId) {
              initialRates[packageId] = { extra_km: '' };
            }
          });
        } catch (error) {
          console.error('Error fetching service packages:', error);
        }
      } else {
        servicePackages.forEach((pkg: any) => {
          const packageId = pkg.id || pkg.attributes?.id;
          if (packageId) {
            initialRates[packageId] = { extra_km: '' };
          }
        });
      }
      setCompanyRates(initialRates);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCompany(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let companyId;
      if (currentCompany) {
        const companyIdToUpdate = currentCompany.id || currentCompany.attributes?.id;
        await insuranceCompanyService.update(companyIdToUpdate, formData);
        companyId = companyIdToUpdate;
      } else {
        const newCompany = await insuranceCompanyService.create(formData);
        companyId = newCompany.id || newCompany.data?.id;
      }
      
      // Update company rates in service packages
      if (companyId) {
        for (const pkg of servicePackages) {
          const packageId = pkg.id || pkg.attributes?.id;
          const packageData = pkg.attributes || pkg;
          const currentRates = typeof packageData.companyRates === 'string' 
            ? JSON.parse(packageData.companyRates) 
            : (packageData.companyRates || {});
          
          // Update rates for this company - always ensure extra_km exists
          const ratesToSave = companyRates[packageId] || {};
          // Ensure extra_km is always included (even if empty)
          if (!ratesToSave['extra_km']) {
            ratesToSave['extra_km'] = '';
          }
          // Always save rates for this company (at minimum, extra_km)
          currentRates[companyId] = ratesToSave;
          
          // Update the package
          await servicePackageService.update(packageId, {
            companyRates: currentRates
          });
        }
      }
      
      handleCloseDialog();
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  };

  const updateCompanyRate = (packageId: string, serviceCode: string, rate: string) => {
    setCompanyRates(prev => ({
      ...prev,
      [packageId]: {
        ...(prev[packageId] || {}),
        [serviceCode]: rate
      }
    }));
  };

  const removeCompanyRate = (packageId: string, serviceCode: string) => {
    // Prevent deletion of extra_km
    if (serviceCode === 'extra_km') {
      return;
    }
    
    setCompanyRates(prev => {
      const newRates = { ...prev };
      if (newRates[packageId]) {
        const packageRates = { ...newRates[packageId] };
        delete packageRates[serviceCode];
        // Always keep extra_km, even if other rates are removed
        if (Object.keys(packageRates).length === 0 || (Object.keys(packageRates).length === 1 && packageRates['extra_km'])) {
          newRates[packageId] = { extra_km: packageRates['extra_km'] || '' };
        } else {
          newRates[packageId] = packageRates;
        }
      }
      return newRates;
    });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await insuranceCompanyService.delete(id);
        fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
      }
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: t('name'),
      flex: 1
    },
    { 
      field: 'code', 
      headerName: t('code'),
      width: 100
    },
    { 
      field: 'excelStructure', 
      headerName: 'Excel Structure',
      width: 150,
      renderCell: (params: any) => (
        <Chip label={params.value?.toUpperCase() || 'N/A'} size="small" color="primary" />
      )
    },
    { 
      field: 'contactPerson', 
      headerName: t('contact'),
      flex: 1
    },
    { 
      field: 'email', 
      headerName: t('email'),
      flex: 1
    },
    { 
      field: 'phone', 
      headerName: t('phone'),
      width: 150
    },
    { 
      field: 'isActive', 
      headerName: t('status'),
      width: 100,
      renderCell: (params: any) => (
        <Chip 
          label={params.value ? t('active') : t('inactive')} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h4">
            {t('insuranceCompanies')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('addCompany')}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            rows={companies}
            loading={loading}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {currentCompany ? t('editCompany') : t('addCompany')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('name')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('code')}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('contactPerson')}
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('email')}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <FormControl fullWidth required>
                    <InputLabel>Excel Structure</InputLabel>
                    <Select
                      value={formData.excelStructure}
                      label="Excel Structure"
                      onChange={(e) => setFormData({ ...formData, excelStructure: e.target.value })}
                    >
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="iran">Iran Company</MenuItem>
                      <MenuItem value="ouic">OUIC</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <TextField
                fullWidth
                label={t('address')}
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Contract Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.contractStartDate}
                    onChange={(e) => setFormData({ ...formData, contractStartDate: e.target.value })}
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Contract End Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.contractEndDate}
                    onChange={(e) => setFormData({ ...formData, contractEndDate: e.target.value })}
                  />
                </Box>
              </Box>
              <TextField
                fullWidth
                label={t('notes')}
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
              
              {/* Service Rates Section */}
              {servicePackages.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Service Package Rates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set custom rates for each service package. These rates will be used when creating invoices for this insurance company.
                  </Typography>
                  
                  {servicePackages.map((pkg) => {
                    const packageId = pkg.id || pkg.attributes?.id;
                    const packageData = pkg.attributes || pkg;
                    const services = Array.isArray(packageData.services) ? packageData.services : [];
                    const packageRates = companyRates[packageId] || {};
                    
                    return (
                      <Card key={packageId} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            {packageData.name || packageData.code} Package
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Service</TableCell>
                                  <TableCell>Service Code</TableCell>
                                  <TableCell>Rate (OMR)</TableCell>
                                  <TableCell width={50}></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {services.map((service: any) => {
                                  const serviceCode = service.code || '';
                                  const serviceName = service.name || serviceCode;
                                  const currentRate = packageRates[serviceCode] || '';
                                  
                                  return (
                                    <TableRow key={serviceCode}>
                                      <TableCell>{serviceName}</TableCell>
                                      <TableCell>
                                        <Chip label={serviceCode} size="small" />
                                      </TableCell>
                                      <TableCell>
                                        <NumberTextField
                                          size="small"
                                          value={currentRate}
                                          onChange={(e) => updateCompanyRate(packageId, serviceCode, e.target.value)}
                                          placeholder="Default rate"
                                          inputProps={{ min: 0, step: 0.001 }}
                                          sx={{ width: 120 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        {currentRate && serviceCode !== 'extra_km' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => removeCompanyRate(packageId, serviceCode)}
                                            color="error"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {/* Always show extra_km rate row */}
                                {(() => {
                                  const extraKmRate = packageRates['extra_km'] || '';
                                  const hasExtraKmInServices = services.some((s: any) => (s.code || '') === 'extra_km');
                                  
                                  if (!hasExtraKmInServices) {
                                    return (
                                      <TableRow>
                                        <TableCell>Extra Kilometer Rate</TableCell>
                                        <TableCell>
                                          <Chip label="extra_km" size="small" />
                                        </TableCell>
                                        <TableCell>
                                          <NumberTextField
                                            size="small"
                                            value={extraKmRate}
                                            onChange={(e) => updateCompanyRate(packageId, 'extra_km', e.target.value)}
                                            placeholder="Default rate"
                                            inputProps={{ min: 0, step: 0.001 }}
                                            sx={{ width: 120 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {/* extra_km cannot be deleted */}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }
                                  return null;
                                })()}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
            <Button type="submit" variant="contained">
              {t('save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default InsuranceCompanies;

